// @flow

import * as React from "react";
import { useInView } from "react-intersection-observer";

import { type ImageType } from "../../types";
import pubSub from "../../utils/pubsub";

type PropsType = {
    src?: string,
    srcset?: string,
    alt?: string,
    className?: string,
    aspectRatio?: string,
    image?: ImageType,
    size?: any,
    style?: Object,
    skeleton?: boolean,
};

const Image = ({ className, ...props }: PropsType): React.Node => {
    const ref = React.useRef();
    const [imageRef, imageRefInView] = useInView({
        triggerOnce: true,
    });

    const cssBaseClass = "image";
    const classList = [cssBaseClass];
    if (className) {
        classList.push(className);
    }
    if (props.skeleton) {
        classList.push(`${cssBaseClass}__skeleton`);
    }

    const checkToLoadImage = () => {
        if (imageRefInView && ref.current) {
            // img load event, set opacity to 1 once loaded
            // ref.current.addEventListener("load", () => {
            //     if (ref.current) {
            //         ref.current.style.opacity = "1";
            //     }
            // });
            const loadCheckInterval = setInterval(() => {
                if (ref.current && ref.current.complete) {
                    ref.current.style.opacity = "1";
                    clearInterval(loadCheckInterval);
                }
            }, 10);

            // set src on image
            if (ref.current) {
                const dataSrc = ref.current.getAttribute("data-src");
                const dataSrcSet = ref.current.getAttribute("data-srcset");
                if (ref.current) {
                    ref.current.srcset = dataSrcSet || "";
                    ref.current.src = dataSrc || "";
                }
            }
        }
    };

    // Use `useCallback` don't recreate the function on each render - Could result in infinite loop
    const setRefs = React.useCallback(
        node => {
            // Ref's from useRef needs to have the node assigned to `current`
            ref.current = node;
            // Callback refs, like the one from `useInView`, is a function that takes the node as an argument
            imageRef(node);
        },
        [imageRef]
    );

    // use effect for when image is in viewport
    React.useEffect(() => {
        checkToLoadImage();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageRefInView]);

    React.useEffect(() => {
        pubSub.subscribe("@@router/LOCATION_CHANGE", () => {
            checkToLoadImage();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // set src and alt props automatically based off image object prop
    let autoProps;
    if (props.image) {
        const src =
            (props.size && props.image.sizes
                ? props.image.sizes[props.size]
                : props.image.url) || props.image.url;
        autoProps = {
            src: src,
            alt: props.image.alt,
        };
    } else {
        autoProps = {
            // src: props.src,
            // srcset: props.srcset,
            alt: props.alt,
        };
    }

    // set initial load in style (fade from 0 opacity)
    let intialStyles = /MSIE|Trident/.test(window.navigator.userAgent)
        ? {
              opacity: 1,
          }
        : {
              opacity: 0,
              transition: "all 300ms",
          };

    // set forced aspect ratio (croppped image)
    let heightPercentage = "auto";
    if (props.aspectRatio) {
        const width = props.aspectRatio
            ? Number(props.aspectRatio.split(":")[0])
            : null;
        const height = props.aspectRatio
            ? Number(props.aspectRatio.split(":")[1])
            : null;

        if (width && height) {
            // calculate aspect ratio as padding bottom percent style
            heightPercentage = `${(height / width) * 100}%`;

            // set image styles to fill aspect ratio area and crop (cover style)
            intialStyles = {
                ...intialStyles,
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                fontFamily: "object-fit: cover",
            };
        }
    }

    return (
        <div
            className={classList.join(" ")}
            style={{
                display: heightPercentage === "auto" ? "inline-flex" : "block",
                paddingBottom: heightPercentage,
                ...props.style,
            }}
        >
            <img
                ref={setRefs}
                alt=""
                {...autoProps}
                data-src={props.src || autoProps.src}
                data-srcset={props.srcset}
                className={`${cssBaseClass}__image`}
                style={{ ...intialStyles }}
            />
        </div>
    );
};

export default Image;
